// theme.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

export type ThemeLoadHooks = {
  onLoaded?: () => void;
  onError?: () => void;
};

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  /** Source URL last applied successfully (without our cache-buster param). Avoids reloading the same sheet when entering modules after landing. */
  private lastLoadedThemeSource: string | null = null;

  constructor(private http: HttpClient) {}

  loadTheme(blobUrl: string, hooks?: ThemeLoadHooks): void {
    const onLoaded = hooks?.onLoaded;
    const onError = hooks?.onError;

    if (!blobUrl) {
      console.warn('No theme URL provided, using default theme from index.html');
      this.lastLoadedThemeSource = null;
      return;
    }

    if (this.lastLoadedThemeSource === blobUrl) {
      if (onLoaded) {
        Promise.resolve().then(() => onLoaded());
      }
      return;
    }

    // Use <link> tag instead of HttpClient to avoid CORS issues
    // CSS loaded via <link> tags don't require CORS headers
    let themeLink = document.querySelector('link[data-theme="dynamic-client-theme"]') as HTMLLinkElement;
    
    if (!themeLink) {
      themeLink = document.createElement('link');
      themeLink.rel = 'stylesheet';
      themeLink.setAttribute('data-theme', 'dynamic-client-theme');
      document.head.appendChild(themeLink);
    }

    // Error handler - use default theme from index.html
    themeLink.onerror = () => {
      console.error('✗ Failed to load theme from:', blobUrl.substring(0, 80) + '...');
      themeLink.remove();
      this.lastLoadedThemeSource = null;
      onError?.();
    };

    // Success handler — drop index fallback so client :root variables take effect immediately
    themeLink.onload = () => {
      document.getElementById('fallback-theme')?.remove();
      this.lastLoadedThemeSource = blobUrl;
      onLoaded?.();
    };

    // Add cache buster to prevent conditional headers (If-Modified-Since, If-None-Match)
    // Azure SAS tokens don't support conditional headers and return 400 error
    const separator = blobUrl.includes('?') ? '&' : '?';
    const urlWithCacheBuster = `${blobUrl}${separator}v=${Date.now()}`;
    
    // Set the href to trigger load
    themeLink.href = urlWithCacheBuster;
  }
}